import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { createExperimentationClient, ExperimentationClient } from 'experimentation-client-js';
import { ExperimentationContext } from './ExperimentationContext';
import { ExperimentationClientConfig, SubjectAttributes } from 'experimentation-client-js';

interface ExperimentationProviderProps {
    /**
     * Configuration for initializing the experimentation client.
     */
    config: ExperimentationClientConfig;

    /**
     * Subject attributes to use for all feature flag evaluations.
     * Can be overridden per hook call if needed.
     */
    subjectAttributes: SubjectAttributes;

    /**
     * Child components that can consume the context.
     */
    children: ReactNode;

    /**
     * Fallback content to render while the client is initializing.
     */
    fallback?: ReactNode;
}

/**
 * Provider component that initializes and exposes the Experimentation Client
 * through React Context.
 *
 * @example
 * ```tsx
 * <ExperimentationProvider config={clientConfig}>
 *   <App />
 * </ExperimentationProvider>
 * ```
 */
export const ExperimentationProvider: FC<ExperimentationProviderProps> = ({ config, subjectAttributes, children }) => {
    const asyncClientRef = useRef(createExperimentationClient(config));
    const [syncClient, setSyncClient] = useState<ExperimentationClient | null>(null);

    useEffect(() => {
        (async () => {
            const client = await asyncClientRef.current;
            setSyncClient(client);
        })();
    }, []);

    return (
        <ExperimentationContext.Provider value={{ syncClient, asyncClient: asyncClientRef.current, subjectAttributes }}>
            {children}
        </ExperimentationContext.Provider>
    );
};
